import { Box, Dialog, IconButton, InputAdornment, lighten, TextField, ThemeProvider, Typography, useMediaQuery } from "@mui/material";
import Theme from "../utils/theme";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../store";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullPageScroller, { type ISectionSpec } from "../components/FullPageScroller";
import ThumbnailStack from "../components/ThumbnailStack";
import type { ISavedRecipes } from "../types/recipe";
import { generateRecipeByName, openRecipeModal as openRecipeModalHelper } from "../utils/helpers/recipeHelper";
import axios from "axios";
import { ArrowBack, AutoAwesome, KeyboardDoubleArrowDown } from "@mui/icons-material";
import Loading from "../components/Loading";
import { scrollbarCss } from "../utils/themeScrollbar";
import ThumbnailRow from "../components/ThumbnailRow";
import SavedRecipe from "../components/SavedRecipe";

// *** Memoized Components  ***
const MemoizedThumbnailStack = memo(ThumbnailStack);
const MemoizedFullPageScroller = memo(FullPageScroller);
const MemoizedThumbnailRow = memo(ThumbnailRow);

// Interfaces //
interface IFeatureCard {
    icon: string;
    title: string;
    desc: string;
    href: string;
    userOnly: boolean;
}

// Constants //
const featureCards = [
    {
        icon: "/images/CommunityFeatureIcon.png",
        title: "Community",
        desc: "Recipes and posts from other cooks!",
        href: "/community",
        userOnly: false,
    },
    {
        icon: "/images/GenerateFeatureIcon.png",
        title: "Generate",
        desc: "Let AI suggest a dish!",
        href: "/generate",
        userOnly: false,
    },
    { icon: "/images/LibraryFeatureIcon.png", title: "Library", desc: "View your saved meals.", href: "/library", userOnly: true },
];

const Home: React.FC = () => {
    // *** Root States *** //
    const authState = useSelector((state: RootState) => state.auth);
    const themeState = useSelector((state: RootState) => state.theme);
    const theme = useMemo(() => Theme(themeState.mode), [themeState.mode]);
    const smUp = useMediaQuery(theme.breakpoints.up("sm"));
    const mdUp = useMediaQuery(theme.breakpoints.up("md"));
    const isXs = useMediaQuery(theme.breakpoints.down("sm"));
    const isLoggedIn = !!authState.account;

    // *** Component States *** //
    const promptRef = useRef<string>("");
    const [recipeLoading, setRecipeLoading] = useState(false);
    const [currentRecipe, setCurrentRecipe] = useState<ISavedRecipes | null>(null);
    const [openRecipeModal, setOpenRecipeModal] = useState(false);
    const [recentRecipes, setRecentRecipes] = useState<ISavedRecipes[]>([]);
    const [currentRecipeIndex, setCurrentRecipeIndex] = useState<number>(0);
    const [trendingRecipes, setTrendingRecipes] = useState<ISavedRecipes[]>([]);
    const [popularRecipes, setPopularRecipes] = useState<ISavedRecipes[]>([]);
    const [randomRecipes, setRandomRecipes] = useState<ISavedRecipes[]>([]);

    const dispatch = useDispatch();

    // *** Use Effects *** //
    // Fetch Recipes with images that were updated recently //
    useEffect(() => {
        // Fetch all recent images on component render //
        axios
            .get(`${process.env.REACT_APP_API_URL}/recipes/fetchRecentImages`, {
                params: { username: isLoggedIn ? authState.account?.username : null },
            })
            .then((res) => {
                setRecentRecipes(res.data.body);
            });
    }, [authState.account?.username, isLoggedIn]);

    // Fetch 30 trending recipes on component render //
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/recipes/trending`).then((res) => {
            setTrendingRecipes(res.data.body);
        });
    }, []);

    // Fetch 30 popular recipes on component render //
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/recipes/popular`).then((res) => {
            setPopularRecipes(res.data.body);
        });
    }, [authState.account?.username, isLoggedIn]);

    // Fetch 30 random recipes on component render //
    useEffect(() => {
        axios.get(`${process.env.REACT_APP_API_URL}/recipes/random`).then((res) => {
            setRandomRecipes(res.data.body);
        });
    }, [authState.account?.username, isLoggedIn]);

    // *** Component Helpers *** //
    const handleRecipeClick = useCallback((recipeId: string) => {
        openRecipeModalHelper(recipeId, setCurrentRecipe, setOpenRecipeModal);
    }, []);

    const handleRecipeGenerate = useCallback(() => {
        if (recipeLoading) return;

        setRecipeLoading(true);

        const value = promptRef.current.trim();

        generateRecipeByName(value, !isLoggedIn ? null : authState.account?.username, dispatch)
            .then(() => {
                window.location.href = "/generate/name";
            })
            .finally(() => setRecipeLoading(false));
    }, [authState.account?.username, dispatch, isLoggedIn, recipeLoading]);

    const renderThumbnailRows = useMemo(
        () => (
            <Box
                sx={{
                    width: "100vw",
                    display: "flex",
                    height: { md: "100dvh" },
                    flexDirection: "column",
                    WebkitOverflowScrolling: "touch",
                    overflowY: { md: "auto" },
                    ...scrollbarCss,
                }}
            >
                {trendingRecipes.length > 0 && (
                    <MemoizedThumbnailRow
                        thumbnailClick={(recipeId: string) => {
                            handleRecipeClick(recipeId);
                        }}
                        rowTitle="Trending Recipes"
                        recipes={trendingRecipes}
                    />
                )}
                {popularRecipes.length > 0 && (
                    <MemoizedThumbnailRow
                        thumbnailClick={(recipeId: string) => {
                            handleRecipeClick(recipeId);
                        }}
                        rowTitle="Popular Recipes"
                        recipes={popularRecipes}
                    />
                )}
                {randomRecipes.length > 0 && (
                    <MemoizedThumbnailRow
                        thumbnailClick={(recipeId: string) => {
                            handleRecipeClick(recipeId);
                        }}
                        rowTitle="Random Recipes"
                        recipes={randomRecipes}
                    />
                )}
            </Box>
        ),
        [handleRecipeClick, popularRecipes, randomRecipes, trendingRecipes]
    );

    const renderFeatureCards = useMemo(
        () => (
            <Box sx={{ display: "flex", cursor: "pointer", width: "100", justifyContent: "center", gap: 5 }}>
                {featureCards.map((feature: IFeatureCard) => {
                    const isDisabled = !isLoggedIn && feature.userOnly;

                    return (
                        <Box
                            component="a"
                            href={feature.href}
                            key={feature.title}
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "center",
                                alignItems: "center",
                                userSelect: "none",
                                textDecoration: "none",
                                cursor: isDisabled ? "not-allowed" : "pointer",
                                opacity: isDisabled ? 0.6 : 1,
                                pointerEvents: isDisabled ? "none" : "auto",
                            }}
                            aria-disabled={isDisabled}
                        >
                            <Box
                                component="img"
                                src={feature.icon}
                                alt="Community Feature Icon"
                                sx={{
                                    width: smUp ? 108 : 64,
                                    height: smUp ? 108 : 64,
                                    borderRadius: "50%",
                                    border: "2px solid " + theme.palette.primary.main,
                                    transition: "box-shadow 0.3s ease, transform 0.3s ease",
                                    boxShadow: "0 0 0px rgba(249, 115, 22, 0)",
                                    "&:hover": {
                                        boxShadow: "0 0 18px 6px rgba(249, 115, 22, 0.45)",
                                        transform: "translateY(-4px)",
                                        cursor: "pointer",
                                    },
                                }}
                            />

                            <Typography
                                variant="subtitle2"
                                sx={{ userSelect: "none", color: theme.palette.primary.main, fontSize: "14px" }}
                            >
                                {feature.title}
                            </Typography>
                        </Box>
                    );
                })}
            </Box>
        ),
        [isLoggedIn, smUp, theme.palette.primary.main]
    );

    const sections = useMemo(() => {
        // Medium viewport sections //
        if (mdUp)
            return [
                {
                    id: "features",
                    bg: "#fff",
                    content: (
                        <Box
                            sx={{
                                height: "calc(100dvh - 69px)",
                                width: "100vw",
                                maxWidth: 1200,
                                display: "flex",
                                flexDirection: "column",
                                p: 4,
                            }}
                        >
                            <Box sx={{ display: "flex", pt: 20 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 4, width: "60%" }}>
                                    <Typography sx={{ userSelect: "none" }} variant="h2">
                                        Time to cook your ideas into{" "}
                                        <Box component="span" sx={{ color: "primary.main" }}>
                                            delicious
                                        </Box>{" "}
                                        meals!
                                    </Typography>
                                    <Typography sx={{ userSelect: "none" }} variant="h6">
                                        How would you like to whip up tastiness today?
                                    </Typography>
                                    {renderFeatureCards}
                                </Box>
                                <Box sx={{ width: "280px" }}></Box>
                                <MemoizedThumbnailStack
                                    clickHandler={(recipeId: string) => {
                                        handleRecipeClick(recipeId);
                                    }}
                                    recipes={recentRecipes}
                                    currentIndex={currentRecipeIndex}
                                    setCurrentIndex={setCurrentRecipeIndex}
                                />
                                <Box sx={{ width: "180px" }}></Box>
                            </Box>
                            <Box sx={{ mt: 8 }}>
                                <Typography sx={{ userSelect: "none" }} variant="h5">
                                    Quick Generate
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Have something in mind? Give us a recipe name or description!"
                                    onChange={(e) => {
                                        promptRef.current = e.target.value;
                                    }}
                                    multiline
                                    minRows={1}
                                    maxRows={2}
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
                                                <IconButton
                                                    onClick={handleRecipeGenerate}
                                                    edge="end"
                                                    sx={{
                                                        cursor: "pointer",
                                                        mr: 0.5,
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: "50%",
                                                        background: (theme) =>
                                                            `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                                        color: "white",
                                                        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                                                        "&:hover": {
                                                            background: (theme) =>
                                                                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                        },
                                                    }}
                                                >
                                                    {recipeLoading ? (
                                                        <Loading color="white" size="20px" />
                                                    ) : (
                                                        <AutoAwesome sx={{ fontSize: 20 }} />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        backgroundColor: lighten(theme.palette.text.secondary, 0.9),
                                        borderRadius: 5,
                                        mt: 1,
                                        textarea: {
                                            ...scrollbarCss,
                                        },
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <KeyboardDoubleArrowDown sx={{ color: theme.palette.primary.main, fontSize: "69px" }} />
                                <Typography variant="h5" sx={{ userSelect: "none", color: theme.palette.primary.main }}>
                                    Scroll down for more!
                                </Typography>
                                <KeyboardDoubleArrowDown sx={{ color: theme.palette.primary.main, fontSize: "69px" }} />
                            </Box>
                        </Box>
                    ),
                },
                {
                    id: "recipes",
                    bg: "#fff",
                    content: renderThumbnailRows,
                },
            ];
        // Small viewport sections //
        else if (smUp)
            return [
                {
                    id: "features",
                    bg: "#fff",
                    content: (
                        <Box sx={{ height: "calc(100dvh - 69px)", display: "flex", flexDirection: "column" }}>
                            <Box sx={{ display: "flex", pt: 12 }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 4 }}>
                                    <Typography variant="h2" sx={{ userSelect: "none", textAlign: "center" }}>
                                        Time to cook your ideas into{" "}
                                        <Box component="span" sx={{ color: "primary.main" }}>
                                            delicious
                                        </Box>{" "}
                                        meals!
                                    </Typography>
                                    <Typography variant="h6" sx={{ userSelect: "none", textAlign: "center" }}>
                                        How would you like to whip up tastiness today?
                                    </Typography>
                                    {renderFeatureCards}
                                </Box>
                            </Box>
                            <Box sx={{ mt: 8, p: 1 }}>
                                <Typography sx={{ userSelect: "none" }} variant="h5">
                                    Quick Generate
                                </Typography>
                                <TextField
                                    fullWidth
                                    placeholder="Have something in mind? Give us a recipe name or description!"
                                    onChange={(e) => {
                                        promptRef.current = e.target.value;
                                    }}
                                    multiline
                                    minRows={1}
                                    maxRows={2}
                                    variant="outlined"
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
                                                <IconButton
                                                    onClick={handleRecipeGenerate}
                                                    edge="end"
                                                    sx={{
                                                        cursor: "pointer",
                                                        mr: 0.5,
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: "50%",
                                                        background: (theme) =>
                                                            `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                                        color: "white",
                                                        boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                                                        "&:hover": {
                                                            background: (theme) =>
                                                                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                        },
                                                    }}
                                                >
                                                    {recipeLoading ? (
                                                        <Loading color="white" size="20px" />
                                                    ) : (
                                                        <AutoAwesome sx={{ fontSize: 20 }} />
                                                    )}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                    sx={{
                                        backgroundColor: lighten(theme.palette.text.secondary, 0.9),
                                        borderRadius: 5,
                                        mt: 1,
                                        textarea: {
                                            ...scrollbarCss,
                                        },
                                    }}
                                />
                            </Box>

                            <Box sx={{ mt: 5 }}>
                                <MemoizedThumbnailStack
                                    clickHandler={(recipeId: string) => {
                                        handleRecipeClick(recipeId);
                                    }}
                                    recipes={recentRecipes}
                                    currentIndex={currentRecipeIndex}
                                    setCurrentIndex={setCurrentRecipeIndex}
                                />
                            </Box>
                            <Box
                                sx={{
                                    width: "100%",
                                    display: "flex",
                                    flexDirection: "column",
                                    flexGrow: 1,
                                    alignItems: "center",
                                    justifyContent: "center",
                                }}
                            >
                                <KeyboardDoubleArrowDown sx={{ color: theme.palette.primary.main, fontSize: "69px" }} />
                            </Box>
                        </Box>
                    ),
                },
                {
                    id: "recipes",
                    bg: "#fff",
                    content: renderThumbnailRows,
                },
            ];

        // Extra small viewport sections //
        return [
            {
                id: "features",
                bg: "#fff",
                content: (
                    <Box sx={{ height: "calc(100dvh - 69px)", display: "flex", flexDirection: "column" }}>
                        <Box sx={{ display: "flex", pt: 12 }}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 4, maxWidth: "100vw" }}>
                                <Typography variant="h5" sx={{ userSelect: "none", textAlign: "center" }}>
                                    Time to cook your ideas into{" "}
                                    <Box component="span" sx={{ color: "primary.main" }}>
                                        delicious
                                    </Box>{" "}
                                    meals!
                                </Typography>
                                <Typography variant="subtitle2" sx={{ userSelect: "none", textAlign: "center" }}>
                                    How would you like to whip up tastiness today?
                                </Typography>
                                {renderFeatureCards}
                            </Box>
                        </Box>
                        <Box sx={{ mt: 2, p: 1 }}>
                            <TextField
                                fullWidth
                                placeholder="Have something in mind? Give us a recipe name or description!"
                                onChange={(e) => {
                                    promptRef.current = e.target.value;
                                }}
                                multiline
                                minRows={1}
                                maxRows={2}
                                variant="outlined"
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 0.5 }}>
                                            <IconButton
                                                onClick={handleRecipeGenerate}
                                                edge="end"
                                                sx={{
                                                    cursor: "pointer",
                                                    mr: 0.5,
                                                    width: 36,
                                                    height: 36,
                                                    borderRadius: "50%",
                                                    background: (theme) =>
                                                        `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                                                    color: "white",
                                                    boxShadow: "0 3px 10px rgba(0,0,0,0.15)",
                                                    "&:hover": {
                                                        background: (theme) =>
                                                            `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                                                    },
                                                }}
                                            >
                                                {recipeLoading ? (
                                                    <Loading color="white" size="20px" />
                                                ) : (
                                                    <AutoAwesome sx={{ fontSize: 20 }} />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    backgroundColor: lighten(theme.palette.text.secondary, 0.9),
                                    borderRadius: 5,
                                    mt: 1,
                                    textarea: {
                                        ...scrollbarCss,
                                    },
                                }}
                            />
                        </Box>
                        <Box
                            sx={{
                                width: "100%",
                                display: "flex",
                                flexDirection: "column",
                                flexGrow: 1,
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <Typography variant="h5" sx={{ userSelect: "none", color: theme.palette.primary.main }}>
                                Scroll down for more!
                            </Typography>
                            <KeyboardDoubleArrowDown sx={{ color: theme.palette.primary.main, fontSize: "69px" }} />
                        </Box>
                    </Box>
                ),
            },
            {
                id: "recipes",
                bg: "#fff",
                content: renderThumbnailRows,
            },
        ];
    }, [
        currentRecipeIndex,
        handleRecipeClick,
        handleRecipeGenerate,
        mdUp,
        recentRecipes,
        recipeLoading,
        renderFeatureCards,
        renderThumbnailRows,
        smUp,
        theme.palette.primary.main,
        theme.palette.text.secondary,
    ]);

    return (
        <ThemeProvider theme={theme}>
            <MemoizedFullPageScroller sections={sections as ISectionSpec[]} />

            <Dialog
                maxWidth="lg"
                fullWidth={!isXs}
                fullScreen={isXs}
                open={openRecipeModal}
                onClose={() => {
                    setOpenRecipeModal(false);
                }}
            >
                {openRecipeModal && currentRecipe !== null && (
                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: "1200px",
                            height: { xs: "100dvh", sm: "90dvh" },
                            p: 3,
                            overflow: "auto",
                            ...scrollbarCss,
                        }}
                    >
                        <IconButton
                            onClick={() => {
                                setCurrentRecipe(null);
                                setOpenRecipeModal(false);
                            }}
                            sx={{ position: "absolute", zIndex: 10, top: 5, left: 5 }}
                        >
                            <ArrowBack />
                        </IconButton>
                        <SavedRecipe
                            recipeData={currentRecipe}
                            setRecipeData={setCurrentRecipe as React.Dispatch<React.SetStateAction<ISavedRecipes>>}
                        />
                    </Box>
                )}
            </Dialog>
        </ThemeProvider>
    );
};

export default Home;
